import { flash, SubmitButton } from "@xola/ui-kit";
import axios, { AxiosRequestConfig } from "axios";
import { FormField, InputField } from "common";
import { FormikProps } from "formik";
import { uniqueId } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { AuthenticationFactorObject, Factor, User } from "types";
import { boolean, object, string } from "yup";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getDefaultAuthenticationFactor, isMultiFactorAuthenticationEnabled } from "../../../models/User";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { MandatoryInformationCheck, MandatoryInformationOptions } from "../MandatoryInformationCheck";
import { AuthenticationFor, MultiFactorAuthenticationModal } from "./MultiFactorAuthenticationModal";

export interface MultiFactorAuthenticationPayload {
    enabled: boolean;
    phone: string;
}

const validationSchema = object().shape({
    enabled: boolean().required(),
    phone: string()
        .matches(/^\+(\d{0,14})$/, "should be valid international format +xxxxxxxxx")
        .required(),
});

export const MultiFactorAuthenticationForm = () => {
    const user = useUser();
    const seller = useSeller();
    const queryClient = useQueryClient();
    const invalidate = useInvalidate();
    const config: AxiosRequestConfig = {};
    const { action } = useParams<{ action?: string }>();
    const [selectedOption, setSelectedOption] = useState<MandatoryInformationOptions | null>(null);

    const multiFactorAuthenticationFormRef = useRef<FormikProps<MultiFactorAuthenticationPayload>>(null);
    const defaultAuthenticationFactor = getDefaultAuthenticationFactor(user);
    const [isMultiFactorAuthenticationModalOpen, setMultiFactorAuthenticationModalOpen] = useState(false);
    const [factorId, setFactorId] = useState<string>();
    const isPhoneChanged =
        defaultAuthenticationFactor &&
        multiFactorAuthenticationFormRef?.current?.values.phone !== defaultAuthenticationFactor?.phone;

    const [id] = useState(uniqueId("mfa-phone"));
    useEffect(() => {
        if (action === "MFASetting") {
            const input = document.querySelector<HTMLElement>(`#${id}`); // TODO: Should use ref
            if (input) {
                setTimeout(() => {
                    input.focus();
                }, 400); // time delay for smoothscroll to MFA
            }
        }
    }, [action, id]);

    const { mutateAsync: addAuthenticationFactor, isLoading } = useMutation(
        async (multiFactorAuthenticationPayload: MultiFactorAuthenticationPayload) => {
            return api.post<Factor>(
                `/users/${user.id}/multiFactorAuthentication/factors?seller=${seller.id}`,
                {
                    phone: multiFactorAuthenticationPayload.phone,
                    object: AuthenticationFactorObject.SMS_AUTHENTICATION_FACTOR,
                },
                config,
            );
        },
    );

    const { mutateAsync: verifyAuthenticationFactor, isLoading: isSavingMFA } = useMutation(async () => {
        return api.put(
            `/users/${user.id}/multiFactorAuthentication/factors/${factorId}?seller=${seller.id}`,
            {
                status: "verified",
            },
            config,
        );
    });

    const handleSubmit = async (values: MultiFactorAuthenticationPayload) => {
        // when first time setting MFA
        if (!defaultAuthenticationFactor) {
            const multiFactorAuthenticationData = await addAuthenticationFactor(values);
            setFactorId(multiFactorAuthenticationData.data.id ?? undefined);
            setMultiFactorAuthenticationModalOpen(true);
            return;
        }

        // Send code to already set default factor number
        setFactorId(defaultAuthenticationFactor.id);
        try {
            await addAuthenticationFactor(values);
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response?.status === 450) {
                setMultiFactorAuthenticationModalOpen(true);
                return;
            }

            throw new Error(error);
        }
    };

    const updateMultiFactorAuthenticationStatus = async (code: string) => {
        const values = multiFactorAuthenticationFormRef?.current?.values;
        if (code && values) {
            invalidate("sellers", seller.id);
            queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
            setMultiFactorAuthenticationModalOpen(false);
            flash.success("Authentication settings successfully changed");
        }
    };

    /**
     * Used to choose the workflow, after Authentication Code is Entered.
     */
    const handleMultiFactorAuthenticationCodeSubmit = async (code: string) => {
        config.headers = { ...config.headers, "X-CONFIRMATION-CODE": code };
        const values = multiFactorAuthenticationFormRef?.current?.values;

        if (code && values) {
            const isDefaultFactor = factorId === defaultAuthenticationFactor?.id;

            // On setting new authentication factor for first time
            if (!defaultAuthenticationFactor) {
                await verifyAuthenticationFactor();
                await updateMultiFactorAuthenticationStatus(code);
                if (action === "MFASetting") {
                    // once MFA setting Done, set next information to check
                    setSelectedOption(MandatoryInformationOptions.KYC_VERIFICATION);
                }

                return;
            }

            // If phone no. already added to Factors, verify that phone no.
            if (isPhoneChanged && !isDefaultFactor) {
                await verifyAuthenticationFactor();
                await updateMultiFactorAuthenticationStatus(code);
                return;
            }

            // If new phone number and not already added
            if (isPhoneChanged) {
                config.headers = { ...config.headers, "X-AUTH-CODE": code };
                // send code to this new phone no. for verification
                const multiFactorAuthenticationData = await addAuthenticationFactor(values);
                setFactorId(multiFactorAuthenticationData.data.id ?? undefined);
                setMultiFactorAuthenticationModalOpen(true);

                flash.success(`Authentication Code Sent to ${values.phone}`);
                invalidate("sellers", seller.id);
                queryClient.invalidateQueries("account"); // Because `account` is not a collection or has ID
                return;
            }

            // If disabled MFA
            config.headers = { ...config.headers, "X-AUTH-CODE": code };
            updateMultiFactorAuthenticationStatus(code);
        }
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                innerRef={multiFactorAuthenticationFormRef}
                validationSchema={validationSchema}
                initialValues={getInitialValues(user)}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, dirty, handleReset }: any) => {
                    const handleCancelClick = async () => {
                        handleReset();
                        if (action === "MFASetting") {
                            setSelectedOption(MandatoryInformationOptions.KYC_VERIFICATION);
                        }
                    };

                    return (
                        <div>
                            <fieldset className="flex-1 space-y-6">
                                <div className="space-y-4">
                                    <h4 className="font-bold">Multi Factor Authentication</h4>
                                    <FormField name="phone" label="Mobile phone">
                                        <InputField isRequired id={id} />
                                    </FormField>
                                </div>

                                <div className="space-x-2 pt-2">
                                    <SubmitButton type="submit" disabled={isLoading || !dirty} onClick={handleSubmit}>
                                        Save
                                    </SubmitButton>

                                    <SecondaryButton type="reset" disabled={isLoading} onClick={handleCancelClick}>
                                        Cancel
                                    </SecondaryButton>
                                </div>
                            </fieldset>
                        </div>
                    );
                }}
            </XolaFormik>

            <MultiFactorAuthenticationModal
                authenticationFor={AuthenticationFor.MFA_SETTING}
                isOpen={isMultiFactorAuthenticationModalOpen}
                factorId={factorId}
                isSaving={isSavingMFA}
                phoneNo={multiFactorAuthenticationFormRef?.current?.values.phone}
                onClose={() => setMultiFactorAuthenticationModalOpen(false)}
                onSuccess={handleMultiFactorAuthenticationCodeSubmit}
            />

            {selectedOption !== null && <MandatoryInformationCheck option={selectedOption} />}
        </>
    );
};

const getInitialValues = (user: User): MultiFactorAuthenticationPayload => {
    return {
        enabled: isMultiFactorAuthenticationEnabled(user),
        phone: getDefaultAuthenticationFactor(user)?.phone ?? "",
    };
};
